import React, { useEffect, useState } from "react";
import {
  Container,
  Card,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
  Input,
  Spinner
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { withTranslation } from "react-i18next";
import axiosInstance from "utils/axiosInstance";

// Select and DND
import Select, { components } from 'react-select';
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

// Custom CSS
import "./InfoCollectionEditor.css";

// Common Components
import ThumbnailUploader from './components/ThumbnailUploader';
import StatusSelect from "./components/StatusSelect";
import HorizontalLoader from 'components/Common/HorizontalLoader';

// Custom Hooks and utils
import { useThumbnail } from 'utils/thumbnails/useThumbnail';
import { fetchThumbnailsWithCache } from "utils/thumbnails/fetchThumbnailsWithCache";
import { useEditableCollectionFields } from './utils/useEditableCollectionFields';

// Placeholders
import placeholderSmall from "assets/images/info/card-placeholder-small.png";


/*----------------------
/* Main Component
/*----------------------*/

const InfoCollectionEditor = ({ t }) => {

  const navigate = useNavigate();
  const { infoCollectionId } = useParams();

  //--------------------------------------------------
  // States
  //--------------------------------------------------
  const [openSelectId, setOpenSelectId] = useState(null);

  const [infoCollection, setInfoCollection] = useState(null);
  const [infoTopics, setInfoTopics] = useState([]);
  /*
  const [infoTopics, setInfoTopics] = useState([
    { order: 1, label: "" },
    { order: 2, label: "" },
    { order: 3, label: "" },
  ]);
  */
  const [infoCollectionLoading, setInfoCollectionLoading] = useState(true);
  const [error, setError] = useState(null);

  const [topicStats, setTopicStats] = useState({});


  //--------------------------------------------------
  // Fetch on mount
  //--------------------------------------------------

  const [thumbnailUrls, setThumbnailUrls] = useState({});
  const [sortedTopics, setSortedTopics] = useState([]);
  const [firstFetching, setFirstFetching] = useState(false);

  const fetchCollectionAndTopics = async () => {
    try {
      setInfoCollectionLoading(true);
      if(sortedTopics.length===0){ setFirstFetching((prev) => true); }
      setError(null);

      // Load collection and topics in parallel
      const [collectionResponse, topicsResponse] = await Promise.all([
        axiosInstance.get(`/knovia/infoCollection/${infoCollectionId}`),
        axiosInstance.get(`/knovia/infoCollection/${infoCollectionId}/getInfoTopics`)
      ]);

      console.log('infoCollection Loaded:', collectionResponse.data);
      console.log('infoTopics in Collection Loaded:', topicsResponse.data);

      setInfoCollection(collectionResponse.data);

      const sorted = [...topicsResponse.data].sort((a, b) => a.order - b.order);
      setSortedTopics(sorted); // Save for later use
      setInfoTopics(sorted);

      // Initial Saved Status
      const initialSavedStatus = {};
      sorted.forEach(topic => {
        initialSavedStatus[topic._id] = true;
      });
      setIsSavedStatus(initialSavedStatus);

    } catch (err) {
      console.error(err);
      setError("Failed to load collection or topics.");
    } finally {
      setInfoCollectionLoading(false);
      setFirstFetching((prev) => false);
    }
  };

  const fetchCompletionStats = async (topics) => {
    try {
      const statsPromises = topics.map(async (topic) => {
        try {
          const res = await axiosInstance.get(`/knovia/infoTopic/${topic._id}/getCompletionStats`);
          return { id: topic._id, stats: res.data };
        } catch (err) {
          console.error(`Failed to fetch stats for ${topic._id}`, err);
          return { id: topic._id, stats: { total: 0, completed: 0 } }; // fallback
        }
      });
      const allStats = await Promise.all(statsPromises);
      const statsMap = {};
      allStats.forEach(({ id, stats }) => {
        statsMap[id] = stats;
      });
      setTopicStats(statsMap);
    } catch (err) {
      console.error(err);
      setError("Failed to load topic completion stats.");
    }
  };

  const fetchThumbnails = async (topics) => {
    try {
      const thumbnailMap = await fetchThumbnailsWithCache(
        topics,
        "infoTopic",
        "sizeSmall",
        ({ id, url }) => {
          setThumbnailUrls(prev => ({
            ...prev,
            [id]: url
          }));
        },
      );
    } catch (err) {
      console.error("Thumbnail fetch failed:", err);
    }
  };

  useEffect(() => {
    fetchCollectionAndTopics();
  }, [infoCollectionId]);

  useEffect(() => {
    if (sortedTopics.length > 0) {
      // Slight delay to allow UI render first
      setTimeout(() => {
        fetchCompletionStats(sortedTopics);
        fetchThumbnails(sortedTopics);
      }, 0);
    }
  }, [sortedTopics]);



  //--------------------------------------------------
  // Editing Collection fields
  //--------------------------------------------------

  const {
    isSaving: isSavingLabel,
    isSaved: isSavedLabel,
    isError: isSavedLabelError,
    handleChange: handleCollectionLabelChange,
    handleSave: handleSaveLabel
  } = useEditableCollectionFields(infoCollection?.label, 'editLabel', 'label', infoCollectionId, axiosInstance, setInfoCollection);

  const {
    isSaving: isSavingDescription,
    isSaved: isSavedDescription,
    isError: isSavedDescriptionError,
    handleChange: handleCollectionDescriptionChange,
    handleSave: handleSaveDescription
  } = useEditableCollectionFields(infoCollection?.description, 'editDescription', 'description', infoCollectionId, axiosInstance, setInfoCollection);



  //--------------------------------------------------
  // ADD/DELETE Topics
  //--------------------------------------------------

  // ADD a new InfoTopic
  const handleAddTopic = async () => {
    try {

      const newTopicPayload = {
        collection: infoCollectionId,
        label: "New Topic"
      };

      const res = await axiosInstance.post("/knovia/infoTopic", newTopicPayload);
      setInfoTopics((prev) => [...prev, res.data]);

    } catch (err) {
      console.error(err);
      setError("Failed to add topic.");
    }
  };

  // DELETE an existing InfoTopic
  const handleDeleteTopic = async (topicId) => {
    try {
      const deletedTopic = infoTopics.find((t) => t._id === topicId);
      if (!deletedTopic) return;

      const deletedOrder = deletedTopic.order;

      // Send delete request
      await axiosInstance.delete(`/knovia/infoTopic/${topicId}`);

      // Update frontend state
      setInfoTopics((prev) => {
        const filtered = prev.filter((t) => t._id !== topicId);
        return filtered.map(t =>
          t.order > deletedOrder ? { ...t, order: t.order - 1 } : t
        );
      });

    } catch (err) {
      console.error(err);
      setError("Failed to delete topic.");
    }
  };



  //--------------------------------------------------
  // Status
  //--------------------------------------------------

  const [isSavingStatus, setIsSavingStatus] = useState({});
  const [isSavedStatus, setIsSavedStatus] = useState({});

  const statusOptions = [
    { value: 'needs_edit', label: '需要編輯' },
    { value: 'in_progress', label: '編輯中' },
    { value: 'finalized', label: '已完成' },
  ];

  const handleStatusChange = async (topicId, newStatus) => {
    // Set saving to true for this specific topic
    setIsSavingStatus(prev => ({ ...prev, [topicId]: true }));
    setIsSavedStatus(prev => ({ ...prev, [topicId]: false }));

    try {
      const res = await axiosInstance.patch(`/knovia/infoTopic/${topicId}/updateStatus`, {
        status: newStatus
      });

      const updatedBy = {
        name: res.data.lastModifiedBy?.username || 'Unknown',
        email: res.data.lastModifiedBy?.email || 'Unknown'
      };

      // Update local infoTopics with the new status
      setInfoTopics(prev =>
        prev.map(topic => 
          topic._id === topicId 
            ? { 
                ...topic, 
                status: newStatus,
                lastStatusUpdate: {
                  updatedAt: new Date(res.data.lastUpdatedAt),
                  updatedBy
                }
              } 
            : topic
        )
      );

      // Mark as saved
      setIsSavedStatus(prev => ({ ...prev, [topicId]: true }));

    } catch (err) {
      console.error('Failed to update status:', err);
      setError("Failed to update topic status.");
    } finally {
      // Clear saving status
      setIsSavingStatus(prev => ({ ...prev, [topicId]: false }));
    }
  };



  //--------------------------------------------------
  // Topic List DND
  //--------------------------------------------------

  const [isSavingTopicOrder, setIsSavingTopicOrder] = useState(false);
  const [isSavedTopicOrder, setIsSavedTopicOrder] = useState(true);
  const [isSavedTopicOrderError, setIsSavedTopicOrderError] = useState(false);

  // Fires when dropping dragged items
  const handleDragEnd = (result) => {

    setIsSavedTopicOrderError(false);
    setIsSavedTopicOrder(false);
    // If there's no valid destination (e.g. dropped outside), do nothing
    if (!result.destination) return;

    // Reorder the array in state
    const newOrder = Array.from(infoTopics);
    // extract the moved item
    const [movedItem] = newOrder.splice(result.source.index, 1);
    // add movedItem to result.destination.index
    newOrder.splice(result.destination.index, 0, movedItem);

    setInfoTopics(newOrder);
    handleSaveTopicOrder(newOrder);
  };

  // Saving new order
  const handleSaveTopicOrder = async (newOrder) => {
    try {

      const payload = newOrder.map((q, index) => ({
        _id: q._id,
        label: q.label,
        order: index + 1,
      }));
      console.log('Saving topic order:', payload);
      setIsSavingTopicOrder(true);

      await axiosInstance.patch(
        `/knovia/infoCollection/${infoCollectionId}/reorderTopics`,
        payload
      );

      // Update local state after successful save
      const reorderedTopics = payload.map(({ _id, order }) => {
        const matchingTopic = infoTopics.find(t => t._id === _id);
        return { ...matchingTopic, order };
      });

      setInfoTopics(reorderedTopics.sort((a, b) => a.order - b.order));

      //alert("Topic order saved!");
      setIsSavingTopicOrder(false);
      setIsSavedTopicOrder(true);

    } catch (err) {
      console.error(err);
      setIsSavingTopicOrder(false);
      setIsSavedTopicOrderError(true);
      setError("Failed to save topic order.");
    }
  };



  //--------------------------------------------------
  // Thumbnails
  //--------------------------------------------------

  const original = useThumbnail("infoCollection", "uploadThumbnail", "viewThumbnail");
  const medium   = useThumbnail("infoCollection", "uploadThumbnail/sizeMedium", "viewThumbnail/sizeMedium");
  const small    = useThumbnail("infoCollection", "uploadThumbnail/sizeSmall", "viewThumbnail/sizeSmall");

  const handleFileChange = async (e, uploadFn, size) => {
    const file = e.target.files[0];
    if (file) {
      await uploadFn(file, infoCollection, axiosInstance, size);
    }
  };

  useEffect(() => {
    if (infoCollection?._id) {
      original.fetchThumbnail(infoCollection, axiosInstance, "sizeOriginal");
      medium.fetchThumbnail(infoCollection, axiosInstance, "sizeMedium");
      small.fetchThumbnail(infoCollection, axiosInstance, "sizeSmall");
    }
  }, [infoCollection?._id]);



  //--------------------------------------------------
  // Render Logic (Not in use)
  //--------------------------------------------------
  /*
  if (infoCollectionLoading) return <div>Loading...</div>;
  if (error) return <div style={{ color: "red" }}>{error}</div>;
  if (!infoCollection) return <div>No InfoCollection found.</div>;
  */


  //--------------------------------------------------
  // Render
  //--------------------------------------------------
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Card>
            <CardBody>

              {/* Title and Button row */}
              <Row>
                <Col xs={4}>
                  <CardTitle className="mb-4">{t("編輯主題集")}</CardTitle>
                </Col>
                <Col xs={8} style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                  <Button color="info" style={{ padding: "7px 8px", marginRight: "0.3rem" }}
                    onClick={(e) => { navigate(`/editInfoThemes/${infoCollection?.theme}`); }}
                  >
                    <i className='bx bx-arrow-back' ></i>{` 回到類別`}
                  </Button>
                  <Button color="primary" style={{ marginRight: "0.3rem" }}>
                    複製主題集
                  </Button>
                </Col>
              </Row>


              {/* Edit Collection Info */}
              <Row style={{ padding: "1rem" }}>
                <Col xs={12}>

                  {/* Collection Label Field */}
                  <Row className="mb-3 mb-md-2">

                    <Col xs={12} md={4} xl={3} className="d-flex align-items-center mb-2 mb-md-0">
                      <h4 className="mb-0 me-3">{t("主題集名稱:", { defaultValue: "主題集名稱:" })}</h4>
                      {isSavingLabel && <span style={{ color: "#f1b34c" }}>Saving...</span>}
                      {!isSavingLabel && !isSavedLabelError &&
                        <span style={{ color: isSavedLabel ? "#34c38f" : "#aeabab" }}>✔ Saved</span>
                      }
                      {!isSavingLabel && isSavedLabelError &&
                        <span style={{ color: "red" }}>Error Saving Label!</span>
                      }
                    </Col>

                    <Col xs={8} md={6} xl={4} className="d-flex">
                      <Input className="me-3"
                        type="text"
                        value={infoCollection?.label || ""}
                        onChange={handleCollectionLabelChange}
                      />
                      <Button color="outline-success" onClick={()=>{handleSaveLabel(infoCollection?.label)}}
                        style={{ padding: "7px 8px" }}
                      ><i className="bx bx-check" /></Button>
                    </Col>
                    
                  </Row>

                  {/* Collection Description Field */}
                  <Row className="mb-3 mb-md-2">

                    <Col xs={12} md={4} xl={3} className="d-flex align-items-center mb-2 mb-md-0">
                      <h4 className="mb-0 me-3">{t("主題集描述:", { defaultValue: "主題集描述:" })}</h4>
                      {isSavingDescription && <span style={{ color: "#f1b34c" }}>Saving...</span>}
                      {!isSavingDescription && !isSavedDescriptionError &&
                        <span style={{ color: isSavedDescription ? "#34c38f" : "#aeabab" }}>✔ Saved</span>
                      }
                      {!isSavingDescription && isSavedDescriptionError &&
                        <span style={{ color: "red" }}>Error Saving Label!</span>
                      }
                    </Col>

                    <Col xs={8} md={6} xl={4} className="d-flex">
                      <Input className="me-3"
                        type="text"
                        value={infoCollection?.description || ""}
                        onChange={handleCollectionDescriptionChange}
                      />
                      <Button color="outline-success" onClick={()=>{handleSaveDescription(infoCollection?.description)}}
                        style={{ padding: "7px 8px" }}
                      ><i className="bx bx-check" /></Button>
                    </Col>

                  </Row>



                  {/* Collection Thumbnails */}
                  <Row>
                    <Col xs={12}>
                      <h4>封面圖片:</h4>
                    </Col>
                  </Row>

                  <ThumbnailUploader
                    original={original}
                    medium={medium}
                    small={small}
                    handleFileChange={handleFileChange}
                  />
                  

                  {/* Horizontal */}
                  <div className="d-flex d-md-none"
                    style={{
                      borderTop: "1px solid #e8e8e8",
                      margin: "30px 0 30px -36px",
                      width: "calc(100% + 72px)",
                    }}
                  />


                  {/* Topic Edit */}

                  <Row style={{ marginBottom: "0.5rem" }}>
                    <Col xs={12} style={{ display: "flex", justifyContent: "space-between", padding: "0" }}>

                      <Row style={{ width: "100%", margin: "0" }}>
                        <Col xs={12} md={4}>
                          <h3>主題集中的主題</h3>
                          {isSavingTopicOrder && <span style={{ color: "#f1b34c" }}>Saving Order...</span>}
                          {!isSavingTopicOrder && !isSavedTopicOrderError &&
                            <span style={{ color: isSavedTopicOrder ? "#34c38f" : "#aeabab" }}>✔ Saved Order</span>
                          }
                          {!isSavingTopicOrder && isSavedTopicOrderError &&
                            <span style={{ color: "red" }}>Error Saving Topic Order!</span>
                          }
                        </Col>
                        <Col xs={12} md={8} style={{ display:"flex", alignItems:"center", justifyContent:"flex-end" }}>
                          <Button color="outline-info" onClick={handleAddTopic} style={{ marginRight: "1rem" }}>
                            + 新增主題
                          </Button>
                        </Col>
                      </Row>

                    </Col>
                  </Row>


                  {/* Topic Drag List */}
                  <Row>
                    <Col xs={12} style={{ padding: "0.5rem 3rem", border: "1px solid #bcbbbb", borderRadius: "5px", minHeight: "13rem" }}>
                      {infoTopics.length === 0 ? (
                        firstFetching ? ( 
                          <HorizontalLoader />
                        ) : (
                          <div className="d-flex flex-column" style={{ gap: "10px", padding: "10px 0px " }}>
                            {[1,2].map((order, index) => {
                              return (
                                <div key={index} style={{
                                  width: "70%",
                                  height: "70px",
                                  border: "1px dashed #3d8dd5",
                                  borderRadius: "5px",
                                }} />
                              )
                            })}
                          </div>
                        )
                      ) : (
                        <DragDropContext onDragEnd={handleDragEnd}>
                          <Droppable droppableId="topicList">
                            {(provided) => (
                              <div
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                              >
                                {infoTopics.map((t, index) => (

                                  <Draggable key={t._id} draggableId={t._id} index={index}>
                                    {(provided) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                      >
                                        {/* Single row content */}
                                        <Row key={t._id} className="draggable-row mt-3 mb-4 mb-mt-0 mb-md-0">

                                          <Col xs={12} md={8} style={{ display: "flex", alignItems: "center", padding: "0.25rem 0rem" }}>
                                            
                                            <div style={{ minWidth: "75px", display: "flex", padding: "0px 15px 0px 0px" }}>
                                              <div
                                                style={{
                                                  height: '60px',
                                                  width: '60px',
                                                  borderRadius: '50%',
                                                  backgroundImage: `url(${thumbnailUrls[t._id] || placeholderSmall})`,
                                                  backgroundSize: 'cover',
                                                  backgroundPosition: 'center',
                                                  transition: 'background-image 0.5s ease-in-out',
                                                }}
                                              />
                                            </div>

                                            <Row style={{ width: "100%", height: "100%", margin: "0", border: "1px solid #bcbbbb", borderRadius: "5px" }}>
                                              <Col xs={12} style={{ padding: 0 }}>
                                                <div style={{ position: "relative", width: "100%", height: "100%", minHeight: "3rem", display: "flex", alignItems: "center" }}>
                                                  {/* Background Bar */}
                                                  <div style={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    width: "100%",
                                                    height: "100%",
                                                    backgroundColor: "#f0f0f0",  // base gray
                                                    borderRadius: "5px",
                                                    zIndex: 0,
                                                  }} />

                                                  {/* Progress Bar */}
                                                  <div style={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    width: `${topicStats[t._id] ? (topicStats[t._id].completed / topicStats[t._id].total) * 100 : 0}%`,
                                                    height: "100%",
                                                    backgroundColor: "#d3eaff", // blue highlight
                                                    borderRadius: "5px 0 0 5px",
                                                    zIndex: 1,
                                                    transition: "width 1.2s ease-in-out"
                                                  }} />

                                                  {/* Text content */}
                                                  <div style={{
                                                    position: "relative",
                                                    zIndex: 2,
                                                    padding: "0.5rem 1rem",
                                                    width: "100%",
                                                  }}>
                                                    <p style={{ margin: 0, fontWeight: 500 }}>
                                                      {`${t.order}. ${t.label}`}{" "}
                                                      {topicStats[t._id] && (
                                                        <span style={{ fontSize: "0.9rem", fontWeight: 400 }}>
                                                          （{topicStats[t._id].completed}/{topicStats[t._id].total} 題組已完成編輯）
                                                        </span>
                                                      )}
                                                    </p>
                                                    {t.lastStatusUpdate && (
                                                      <div style={{ fontSize: "0.75rem", color: "#666", marginTop: "0.2rem", lineBreak: 'anywhere' }}>
                                                        最近狀態由 <strong>{t.lastStatusUpdate.updatedBy.name}</strong>（{t.lastStatusUpdate.updatedBy.email}）<br />
                                                        於 {new Date(t.lastStatusUpdate.updatedAt).toLocaleString()} 更新
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>
                                              </Col>
                                            </Row>

                                          </Col>


                                          {/* Edit and Delete Topics */}
                                          <Col xs={4} sm={6} md={2} 
                                            style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                                          >
                                            <Button color="outline-info" style={{ padding: "7px 8px", marginRight: "0.3rem" }}
                                              onClick={(e) => { navigate(`/editInfoTopics/${t._id}`); }}
                                            >
                                              <i className="bx bxs-pencil" />
                                            </Button>

                                            <Button color="outline-danger" onClick={() => handleDeleteTopic(t._id)}>
                                              -
                                            </Button>
                                          </Col>


                                          {/* Topic Edit Status */}
                                          <Col xs={8} sm={6} md={2}
                                            style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}
                                          >
                                            <div className="w-100 px-1 px-md-0">
                                              <StatusSelect
                                                value={t.status}
                                                onChange={(newStatus) => handleStatusChange(t._id, newStatus)}
                                                options={statusOptions}
                                                selectId={t._id}
                                                openSelectId={openSelectId}
                                                setOpenSelectId={setOpenSelectId}
                                              />
                                            </div>

                                            <div className="w-100 px-1 px-md-0">
                                              {isSavingStatus[t._id] && <span style={{ color: "#f1b34c" }}>Saving...</span>}
                                              {!isSavingStatus[t._id] && (
                                                <span style={{ color: isSavedStatus[t._id] ? "#34c38f" : "#aeabab" }}>
                                                  ✔ Saved!
                                                </span>
                                              )}
                                            </div>

                                          </Col>

                                        </Row>
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        </DragDropContext>
                      )}

                    </Col>
                  </Row>


                </Col>
              </Row>


            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
}

export default withTranslation()(InfoCollectionEditor);




